import React, {useState} from 'react';
import {Modal, Checkbox, Button} from 'antd';
import {DownloadOutlined} from '@ant-design/icons';
import {ModelDTO} from 'hemwb-api';
import {InformationClassificationIcon} from '../../../icons/classification/InformationClassificationIcon';
import styles from './ModelFileDownloadModal.module.scss';

interface ModelFileDownloadModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  model: ModelDTO;
  downloadType: 'all' | 'single';
  fileName?: string;
}

/**
 * Modal for confirming file downloads with legal notice
 * Used for both individual files and "Download All"
 */
export const ModelFileDownloadModal: React.FC<ModelFileDownloadModalProps> = ({
  visible,
  onClose,
  onConfirm,
  model,
  downloadType,
  fileName,
}) => {
  const [agreed, setAgreed] = useState(false);

  const handleConfirm = () => {
    if (agreed) {
      onConfirm();
      setAgreed(false); // Reset for next time
    }
  };

  const handleClose = () => {
    setAgreed(false);
    onClose();
  };

  const title = downloadType === 'all' ? 'Download Model Files' : `Download ${fileName || 'File'}`;

  return (
    <Modal
      title={
        <div className={styles.modalTitle}>
          <div className={styles.titleLeft}>
            <DownloadOutlined className={styles.downloadIcon} />
            <span>{title}</span>
          </div>
          <InformationClassificationIcon
            classification={model.metadata.information_classification}
            markOnly={false}
            className={styles.classificationIcon}
          />
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleConfirm} disabled={!agreed}>
          Download
        </Button>,
      ]}
      width={600}
      className={styles.downloadModal}
    >
      <div className={styles.modalBody}>
        <p className={styles.legalText}>
          Files contain certain proprietary methods, formulas, processes, techniques, and background technologies
          (collectively, "Material") developed by Merck & Co., Inc. In accordance with Company policies, additional
          approvals for authorized disclosure or transfer outside of Merck & Co., Inc. may be required. Use and
          modifications according to BARDS B-S022 Model Adaptation Guides are permitted.
        </p>

        <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className={styles.checkbox}>
          I have read the legal notice
        </Checkbox>
      </div>
    </Modal>
  );
};
